<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useUxData } from "@/stores/ux-data";
import { useAppDataStore } from "@/stores/app-data";
import { useCharacterDataStore } from "@/stores/character-data";
import { usePredicateStore } from "@/stores/predicate-store";
import { useElementPredicates } from '@/composables/useElementPredicates';
import { usePausedSearch } from '@/composables/usePausedSearch';
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import type { SwadeCharacterInterface } from "@/types/characters";
import type { SwadeAncestryInterface } from "@/types/items";
import {handleError} from "@/appdata/globals.ts";
import {useAuthDataStore} from "@/stores/auth-data.ts";
import SearchField from '@/components/atoms/SearchField.vue';
import Pagination from '@/components/data/Pagination.vue';
import FilterBar from '@/components/atoms/element-filters/FilterBar.vue';
import FilterToggle from '@/components/atoms/element-filters/FilterToggle.vue';
import GuidedScopeFilters from '@/components/atoms/element-filters/GuidedScopeFilters.vue';

const props = defineProps<{
    stepIndex: number;
}>();

const character = defineModel<SwadeCharacterInterface | null>('character');

const ux = useUxData();
const store = useAppDataStore();
const auth = useAuthDataStore();
const characterData = useCharacterDataStore();
const predicateStore = usePredicateStore();
const setupStore = useGuidedSetupStore();

// Own predicates rather than the shared predicateStore.abilityFilters slot — that slot
// belongs to the abilities list, and this step only ever wanted a name search out of it.
const { filters, predicates, resetFilters } = useElementPredicates('ancestries');

const ancestries = ref<SwadeAncestryInterface[]>([]);
const selectedAncestry = ref<SwadeAncestryInterface | null>(null);
const ancestryPreviewRef = ref<HTMLElement | null>(null);
const metadata = ref<{ total: number | null; start: number | null; end: number | null }>(
    { total: null, start: null, end: null }
);
const links = ref<{ next: string | null; previous: string | null; current: string | null }>(
    { next: null, previous: null, current: null }
);

// The "this setting has no ancestries" alert is for a setting that has none at all, which only
// an UNFILTERED fetch can establish. A keyword, source or world filter that returns nothing must
// show the empty-results message (with the filters still on screen to undo), not the alert.
// Set per fetch rather than once at mount: going Back remounts this step with the filters the
// user picked on the later steps already applied to its first fetch.
const noAncestries = ref(false);

const filterBarRef = ref<InstanceType<typeof FilterBar> | null>(null);
// SearchField is uncontrolled; bumping its key remounts it empty on "Clear filters".
const searchKey = ref(0);

// Source (the element's own `source` property — where it was published) is orthogonal to
// campaign setting and useful for every draft, so the Source filter is always offered. World
// (which settings an element is linked to) only makes sense for a worldless draft (the world
// step was skipped) — a draft already scoped to a world doesn't need a second, redundant world
// picker. That is the same rule as useGuidedElementPicker's showWorldFilter, computed here
// because this step fetches by hand. See WorldFilterControl.vue's header comment for the full
// source-vs-world distinction.
// The world selection lives in predicateStore.worlds and the source selection in
// predicateStore.sources; both are carried between the steps, and CharacterSetupFlow seeds them
// from the draft's world and resets them when it changes (see the fetch watcher below).
const showWorldFilter = computed(() => !character.value?.world);
const filtersActive = computed(() =>
    predicateStore.sources.length > 0
    // A world-scoped draft's predicateStore.worlds holds the character's own scope, not a user
    // filter — only count it once the world control is actually offered.
    || (showWorldFilter.value && predicateStore.worlds.length > 0)
    || (filters.value.stringPartial?.length ?? 0) > 0
);

const isValid = computed(() => {
    if (noAncestries.value) return true;
    // Only the unfiltered single-ancestry setting auto-validates; a filter narrowed down
    // to one row still needs a deliberate pick.
    if (!filtersActive.value && (metadata.value?.total ?? ancestries.value.length) === 1) return true;
    return selectedAncestry.value !== null;
});

watch(isValid, (valid) => {
    setupStore.validateStep(props.stepIndex, valid);
}, { immediate: true });

const columns = computed(() => [
    { field: 'name', header: ux.labels?.swade?.data?.ancestries?.name ?? 'Name',
      columnClass: ['name-column'] },
    { field: 'summary', header: ux.labels?.swade?.data?.ancestries?.summary ?? 'Summary' },
]);

// The search asks once the typing has paused (usePausedSearch): the text is in the filter at
// once, a keyup that leaves it as it was is no search, and whatever is out while the user types
// does not matter — fetchAncestries shows only the latest request's answer. It used to ask at
// each keyup unless the app was loading something, and so threw away every key typed while a
// request was out.
const { onSearch, cancelSearch } = usePausedSearch({
    keyword: computed({
        get: () => filters.value.stringPartial ?? '',
        set: (value) => { filters.value.stringPartial = value; },
    }),
    search: () => { fetchAncestries(); },
});

// Responses can land out of order — a chip removed while the first fetch is still in flight
// starts a second one — so only the latest request may update the list. Resolves to whether
// this request was that one.
let latestFetch = 0;

// "The latest request is still out": the list on screen is about to be replaced. The list shows
// its spinner for it and the pager waits for it (see Pagination.vue). The app-wide loading flag
// will not do for either: the first answer to land switches it off, whatever else is still out,
// and a page turn never raises it.
const loading = ref(false);

const fetchAncestries = async (url: string | null = null): Promise<boolean> => {
    const sequence = ++latestFetch;
    // A first page is built from the filters as they are now, the typed text among them, so a
    // search still waiting on its pause would only ask for the same thing again.
    if (!url) cancelSearch();
    const unfiltered = !filtersActive.value;
    loading.value = true;
    let response;
    try {
        response = await characterData.initializeElementChoices(
            'ancestries', url, url ? undefined : predicates.value, !url
        );
    } finally {
        if (sequence === latestFetch) loading.value = false;
    }
    if (sequence !== latestFetch) return false;

    ancestries.value = response?.results ?? [];
    metadata.value = response?.metadata ?? { total: null, start: null, end: null };
    links.value = response?.links ?? { next: null, previous: null, current: null };
    // Only a first page that actually answered can say the setting has none: a later page
    // cannot be empty, and a failed request (the store resolves undefined) is "unknown", not
    // "none".
    if (!url) {
        noAncestries.value = unfiltered && !!response && (metadata.value.total ?? ancestries.value.length) === 0;

        // A setting with exactly one ancestry picks it for the user; a filter narrowed down to one
        // row still needs a deliberate pick (see isValid). `unfiltered` was captured before the
        // request: a chip the user clears while a filtered fetch is in flight must not turn its
        // one-row result into a pick, and nor must an unfiltered one under text typed since.
        // Skipped when that one is already the selection (going Back after it was applied):
        // onSelectAncestry toggles, and would clear it.
        // The pick is made here, for whichever request the answer belongs to. It used to be made
        // for the step's opening request only, so a search or a cleared filter that superseded
        // that request left the step valid with nothing picked.
        if (
            unfiltered && !filtersActive.value && ancestries.value.length === 1
            && ancestries.value[0].uuid !== selectedAncestry.value?.uuid
        ) {
            onSelectAncestry(ancestries.value[0]);
        }
    }
    return true;
};

// The draft's own world reaches predicateStore.worlds through CharacterSetupFlow, which seeds
// the slot whenever the draft's world changes — before this step mounts. This step never writes
// the slot: for a worldless draft it holds the World filter the user may have picked on a later
// step, and going Back remounts this step with that pick still in place. So this watcher only
// fetches: at mount, and again should the draft's world change while the step is showing.
watch(() => character.value?.world, () => {
    resetFilters();
    fetchAncestries();
}, { immediate: true });

// SourceFilterControl writes straight to the predicate store; this step fetches by hand
// (no useElementGrid predicate watcher), so refetch when the chip changes.
watch(() => predicateStore.sources.map((source) => source.uuid).join(','), () => {
    fetchAncestries();
});

// WorldFilterControl also writes straight to the predicate store; same deal, refetch when it
// changes.
watch(() => predicateStore.worlds.map((world) => world.uuid).join(','), () => {
    fetchAncestries();
});

const ancestryApplied = ref(false);

const onSelectAncestry = (ancestry: SwadeAncestryInterface) => {
  if (ancestry.uuid === selectedAncestry.value?.uuid) {
    selectedAncestry.value = null;
  }
  else {
    store.retrieveElement(ancestry)
        .then(async (selected: SwadeAncestryInterface) => {
          selectedAncestry.value = selected;
          ancestryApplied.value = false;
          await nextTick();
          ancestryPreviewRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        })
        .catch((error) => { handleError(error) })
  }
};

onBeforeUnmount(() => {
    if (selectedAncestry.value && character.value && !ancestryApplied.value && setupStore.currentStep > props.stepIndex) {
        characterData.applyAncestry(character.value, selectedAncestry.value);
        ancestryApplied.value = true;
    }
});

const clearFilters = () => {
    // Dropping a source or world chip already refetches through its own watcher; fetch here
    // only when neither changed (e.g. the keyword was the sole active filter).
    const hadSources = predicateStore.sources.length > 0;
    // A world-scoped draft's predicateStore.worlds is the character's own scope, not a user
    // filter (see filtersActive) — clearing it here would silently widen the list, so only touch
    // it when the world control is actually offered.
    const hadWorlds = showWorldFilter.value && predicateStore.worlds.length > 0;
    predicateStore.sources = [];
    if (showWorldFilter.value) predicateStore.worlds = [];
    filters.value.stringPartial = '';
    searchKey.value++;
    if (!hadSources && !hadWorlds) fetchAncestries();
};

// The pager itself waits while a request is out (its `loading`).
const onNextPage = () => {
    if (links.value.next) {
        fetchAncestries(links.value.next);
    }
};

const onPreviousPage = () => {
    if (links.value.previous) {
        fetchAncestries(links.value.previous);
    }
};

onMounted(() => {
    if (character.value?.ancestry) {
        selectedAncestry.value = character.value.ancestry as SwadeAncestryInterface;
        ancestryApplied.value = true;
    }
});
</script>

<template>
    <div class="character-ancestry-step">
        <div v-if="noAncestries" class="alert alert-info">
            <span class="fa-light fa-info-circle me-2"></span>
            <span v-html="ux.labels?.guidedSetup?.character?.noAncestries"></span>
        </div>

        <div v-else class="form-group">
            <label class="form-label fw-semibold" v-html="ux.labels?.guidedSetup?.character?.ancestryLabel"></label>
            <p class="form-text mt-0 mb-2" v-html="ux.labels?.guidedSetup?.character?.ancestryHelp"></p>

            <div class="row">
                <div class="col-lg">
                    <div class="data-list-bar d-flex justify-content-between align-items-center mb-2">
                        <div class="keyword-search me-3 mt-auto flex-grow-1">
                            <SearchField
                                :key="searchKey"
                                :placeholder="ux.labels?.guidedSetup?.character?.searchAncestries"
                                @keyword-change="onSearch"
                            />

                            <FilterBar
                                ref="filterBarRef"
                                class="filter-chips"
                                :active="filtersActive"
                                @clear="clearFilters"
                            >
                                <GuidedScopeFilters :show-world="showWorldFilter" />
                            </FilterBar>
                        </div>

                        <FilterToggle
                            class="me-3"
                            :active="filtersActive"
                            @open="filterBarRef?.open()"
                        />

                        <div class="pagination">
                            <Pagination
                                v-if="metadata.total"
                                :start="metadata.start"
                                :end="metadata.end"
                                :total="metadata.total"
                                :disable-next="links.next === null"
                                :disable-previous="links.previous === null"
                                :loading="loading"
                                @next="onNextPage"
                                @previous="onPreviousPage"
                            />
                        </div>
                    </div>

                    <div v-if="loading" class="text-center py-4">
                        <span class="fa-light fa-fw fa-circle-notch fa-spin"></span>
                    </div>
                    <table v-else-if="ancestries.length > 0" class="ancestry-table table table-sm table-hover mb-0">
                        <thead>
                            <tr>
                                <th
                                    v-for="column in columns"
                                    :key="column.field"
                                    class="text-start"
                                    scope="col"
                                >{{ column.header }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="ancestry in ancestries"
                                :key="ancestry.uuid"
                                role="button"
                                :class="{ 'table-active': ancestry.uuid === selectedAncestry?.uuid }"
                                @click="onSelectAncestry(ancestry)"
                            >
                                <td class="name-column">{{ ancestry.name }}</td>
                                <td :data-header="ux?.labels?.swade?.data?.ancestries?.summary ?? 'Summary'">{{ ancestry.summary ?? '&mdash;' }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="text-center py-4 text-muted">
                        {{ ux.labels?.common?.emptyMessage ?? 'No results found.' }}
                    </div>
                </div>

                <div class="col-lg-5 order-md-last order-first">
                    <div v-if="selectedAncestry" ref="ancestryPreviewRef" class="ancestry-preview p-3 border rounded">
                      <p class="h3">{{ selectedAncestry.name }}</p>
                      <blockquote v-if="selectedAncestry.source?.name">
                        <em v-html="selectedAncestry.source.name"></em>
                      </blockquote>
                        <p v-if="selectedAncestry.summary" v-html="selectedAncestry.summary" class="small"></p>

                        <div v-if="(selectedAncestry.grants?.length ?? 0) > 0">
                            <h6 class="mt-3" v-html="ux.labels?.guidedSetup?.character?.grantedAbilities"></h6>
                            <ul class="ability-list mb-0">
                                <li v-for="grant in selectedAncestry.grants" :key="grant.uuid" class="mb-2">
                                    <span class="ability-title">{{ grant.name }}</span>
                                    <span v-if="auth.user?.subscriber && grant.summary" v-html="grant.summary" class="ability-description"></span>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div v-else class="ancestry-preview p-3 border rounded bg-light">
                        <p class="text-muted mb-0 text-center" v-html="ux.labels?.guidedSetup?.character?.selectAnAncestry"></p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
